// src/pages/RevenuePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { revenueAPI } from '../services/api';
import { LoadingOverlay, EmptyState, StatusBadge, toast } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function RevenuePage() {
  const today = moment().format('YYYY-MM-DD');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  const load = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try { setData(await revenueAPI.getReport(from, to)); }
    catch { toast.error('Failed to load revenue report.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setPreset = (preset) => {
    let from, to = today;
    if (preset === 'today') { from = today; }
    else if (preset === 'week') { from = moment().startOf('isoWeek').format('YYYY-MM-DD'); }
    else if (preset === 'month') { from = moment().startOf('month').format('YYYY-MM-DD'); }
    else if (preset === 'year') { from = moment().startOf('year').format('YYYY-MM-DD'); }
    setDateFrom(from); setDateTo(to); load(from, to);
  };

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Honcho Xpress Revenue Report</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; padding: 32px; color: #0f172a; font-size: 13px; }
      h1 { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
      .sub { color: #64748b; margin-bottom: 24px; font-size: 12px; }
      .summary { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-bottom: 24px; }
      .scard { border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; }
      .scard-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px; }
      .scard-val { font-size: 22px; font-weight: 800; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; }
      th { padding: 8px 10px; text-align: left; font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; background: #f8fafc; border-bottom: 1.5px solid #e2e8f0; }
      td { padding: 10px 10px; border-bottom: 1px solid #f1f5f9; font-size: 12px; }
      tr:last-child td { border-bottom: none; }
      .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
      .cash { background: #FEF9EC; color: #B45309; }
      .transfer { background: #EFF6FF; color: #1D4ED8; }
      @media print { body { padding: 16px; } }
    </style></head><body>${content}
    <script>setTimeout(()=>{window.print();},300);<\/script></body></html>`);
    w.document.close();
  };

  const s = data?.summary;
  const fmt = (n) => '₦' + parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1 className="page-title">Revenue Report</h1>
          <p className="page-sub">Filter by date range to see order details and payment breakdown</p>
        </div>
        {data && (
          <button className="btn btn-secondary btn-md" onClick={handlePrint}>🖨️ Print / Export PDF</button>
        )}
      </div>

      {/* Date controls */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ paddingTop: 16, paddingBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Quick presets */}
            <div style={{ display: 'flex', gap: 6 }}>
              {['today', 'week', 'month', 'year'].map(p => (
                <button key={p} className="btn btn-ghost btn-sm" style={{ textTransform: 'capitalize' }} onClick={() => setPreset(p)}>{p}</button>
              ))}
            </div>
            <div style={{ width: 1, height: 28, background: 'var(--gray-200)' }} />
            {/* Date range */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: 11 }}>From</label>
                <input className="form-input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 160, padding: '7px 10px' }} />
              </div>
              <span style={{ color: 'var(--gray-400)', marginTop: 18 }}>—</span>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: 11 }}>To</label>
                <input className="form-input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 160, padding: '7px 10px' }} />
              </div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 18 }} onClick={() => load()}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {loading ? <LoadingOverlay message="Loading report…" /> : !data ? null : (
        <div ref={printRef}>
          {/* Print header (hidden on screen) */}
          <div style={{ display: 'none' }} className="print-header">
            <h1>Honcho Xpress — Revenue Report</h1>
            <p className="sub">{moment(dateFrom).format('MMM D, YYYY')} – {moment(dateTo).format('MMM D, YYYY')} · Generated {moment().format('MMM D, YYYY h:mm A')}</p>
          </div>

          {/* Summary cards */}
          <div className="summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14, marginBottom: 20}}>
            {[
              { label: 'Total Revenue', value: fmt(s.total_revenue), color: 'var(--accent)', cls: 'accent' },
              { label: 'Collected', value: fmt(s.collected_revenue), color: 'var(--success)', cls: 'success' },
              { label: 'Pending Payment', value: fmt(s.pending_revenue), color: 'var(--warning)', cls: 'warning' },
              { label: 'Cash Revenue', value: fmt(s.cash_revenue), color: '#B45309', cls: 'accent' },
              { label: 'Transfer Revenue', value: fmt(s.transfer_revenue), color: '#1D4ED8', cls: 'info' },
              { label: 'Total Orders', value: s.total_orders, color: 'var(--primary)', cls: 'accent' },
              { label: 'Delivered', value: s.delivered, color: 'var(--success)', cls: 'success' },
              { label: 'Failed', value: s.failed, color: 'var(--danger)', cls: 'danger' },
            ].map((c, i) => (
              <div key={c.label} className={`stat-card ${c.cls} animate-fade-up delay-${Math.min(i + 1, 6)}`}>
                <div className="stat-label">{c.label}</div>
                <div className="stat-value" style={{ fontSize: 24, color: c.color }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Payment method breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
           
          </div>

          {/* Orders table */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">All Orders in Range</span>
              <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{data.orders.length} orders</span>
            </div>
            {data.orders.length === 0
              ? <EmptyState icon="" title="No orders" subtitle="No orders in this date range" />
              : <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>ID</th><th>Package ID</th><th>Receiver</th><th>Type</th>
                    <th>Rider</th><th>Payment</th><th>Fee</th><th>Status</th><th>Date</th>
                  </tr></thead>
                  <tbody>
                    {data.orders.map(o => (
                      <tr key={o.id}>
                        <td className="td-mono">#{o.id}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>{o.package_id}</td>
                        <td style={{ fontWeight: 600, fontSize: 13 }}>{o.receiver_name}</td>
                        <td><span style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>{o.package_type}</span></td>
                        <td style={{ fontSize: 12 }}>{o.rider_name || '—'}</td>
                        <td>
                          <span className={`badge ${o.payment_method === 'Cash' ? 'cash' : 'transfer'}`} style={{
                            background: o.payment_method === 'Cash' ? '#FEF9EC' : '#EFF6FF',
                            color: o.payment_method === 'Cash' ? '#B45309' : '#1D4ED8',
                            padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                          }}>
                            {o.payment_method === 'Cash' ? '' : ''} {o.payment_method}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700 }}>₦{parseFloat(o.delivery_fee || 0).toLocaleString()}</td>
                        <td><StatusBadge status={o.status} /></td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--gray-400)' }}>{moment(o.created_at).format('MMM D, h:mm A')}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--gray-50)', fontWeight: 800 }}>
                      <td colSpan={6} style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-600)' }}>Total ({data.orders.length} orders)</td>
                      <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--primary)', fontWeight: 800 }}>
                        ₦{data.orders.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0).toLocaleString()}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>}
          </div>
        </div>
      )}
    </div>
  );
}